import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Loader2, CheckCircle, ArrowLeft, Play, Pause, RotateCcw } from 'lucide-react';
import { loadTrainingDrill, type TrainingDrill } from '@/lib/training-program/drill-loader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { GolfButton } from '@/components/golf/golf-button';
import { GolfCard, GolfCardContent, GolfCardHeader, GolfCardTitle } from '@/components/golf/golf-card';

/**
 * LibraryDrillExecutionPage - Drill execution page for the Drill Library context
 * 
 * UX-AUDIT-V3-100: This page allows execution of ANY drill from the library.
 * UX-AUDIT-V3-101: Returns to library after completion (not training program).
 */
export default function LibraryDrillExecutionPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState<TrainingDrill | null>(null);
  const [currentSet, setCurrentSet] = useState(1);
  const [isRunning, setIsRunning] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  // Track origin for return-to-origin after execution
  const fromContext = (location.state as { from?: string })?.from || 'library';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!slug) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const loadedDrill = await loadTrainingDrill(slug);
        if (!cancelled) {
          setDrill(loadedDrill);
        }
      } catch (error) {
        console.error('Failed to load drill:', error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const totalSets = drill?.assigned_reps?.sets || 3;

  const handleCompleteSet = useCallback(() => {
    if (currentSet >= totalSets) {
      setIsComplete(true);
      setIsRunning(false);
    } else {
      setCurrentSet((prev) => prev + 1);
    }
  }, [currentSet, totalSets]);

  const handleReset = () => {
    setCurrentSet(1);
    setIsComplete(false);
    setIsRunning(false);
  };

  const handleFinish = () => {
    // UX-AUDIT-V3-101: Return to library, not training program
    if (fromContext === 'library') {
      navigate('/drills');
    } else {
      navigate('/training-program');
    }
  };

  const handleExit = () => {
    // Return to appropriate context
    if (fromContext === 'library') {
      navigate('/drills');
    } else {
      navigate('/training-program');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!drill) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <Card className="w-full max-w-md text-center">
          <CardContent className="p-8 space-y-3">
            <h2 className="text-xl text-[#0D382A] font-semibold">Drill Not Found</h2>
            <p className="text-sm text-gray-600">This drill could not be loaded.</p>
            <Button onClick={() => navigate('/drills')} className="w-full">
              Back to Drill Library
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isComplete) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <Card className="w-full max-w-md text-center">
          <CardContent className="p-8 space-y-4">
            <div className="mx-auto h-16 w-16 rounded-full bg-[#196644]/10 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-[#196644]" />
            </div>
            <h2 className="text-2xl text-[#0D382A] font-semibold">Great Work!</h2>
            <p className="text-sm text-gray-600">
              You've completed {drill.drill_name}
            </p>
            <div className="space-y-2 pt-4">
              <Button onClick={handleFinish} className="w-full">
                Back to Drill Library
              </Button>
              <Button variant="outline" onClick={handleReset} className="w-full">
                Practice Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#0D382A] to-[#196644] text-white">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={handleExit}
                className="h-9 w-9 rounded-full border border-white/30 flex items-center justify-center hover:bg-white/10"
                aria-label="Exit"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div>
                <p className="text-sm text-white/70">Drill in Progress</p>
                <h1 className="text-xl font-semibold">{drill.drill_name}</h1>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-white/70">Set</p>
              <p className="text-2xl font-bold">{currentSet}/{totalSets}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Swing Thought */}
        <GolfCard className="bg-gradient-to-r from-[#0D382A] to-[#196644] text-white">
          <GolfCardHeader>
            <GolfCardTitle className="text-white text-sm uppercase tracking-wide">Keep This in Mind</GolfCardTitle>
          </GolfCardHeader>
          <GolfCardContent>
            <p className="text-2xl font-semibold italic text-center">
              {drill.swing_thought || 'Stay focused on your swing thought.'}
            </p>
          </GolfCardContent>
        </GolfCard>

        {/* Current Set Progress */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#0D382A]">Set {currentSet} of {totalSets}</h3>
              <span className="text-sm text-gray-500">
                {drill.assigned_reps?.reps_per_set || 10} reps
              </span>
            </div>
            
            {/* Progress bar */}
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-6">
              <div 
                className="h-full bg-gradient-to-r from-[#196644] to-[#1B7D52] transition-all duration-300"
                style={{ width: `${(currentSet / totalSets) * 100}%` }}
              />
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setIsRunning(!isRunning)}
                className="flex-1 gap-2"
              >
                {isRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {isRunning ? 'Pause' : 'Start Set'}
              </Button>
              <Button 
                onClick={handleCompleteSet}
                className="flex-1"
              >
                Complete Set
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Quick Reminders */}
        {drill.what_to_look_for && drill.what_to_look_for.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Quick Reminders</h4>
              <ul className="space-y-1">
                {drill.what_to_look_for.slice(0, 3).map((item, i) => (
                  <li key={i} className="text-sm text-[#0D382A] flex gap-2">
                    <span className="text-[#196644]">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 pb-safe">
        <div className="flex gap-3 max-w-7xl mx-auto">
          <Button variant="outline" onClick={handleReset} className="gap-2">
            <RotateCcw className="w-4 h-4" />
            Reset
          </Button>
          <GolfButton onClick={handleCompleteSet} className="flex-1">
            {currentSet >= totalSets ? 'Finish Drill' : 'Complete Set'}
          </GolfButton>
        </div>
      </div>
    </div>
  );
}
